import { Injectable, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CollaborationItem, Dataset } from '../models/collaboration.model';

@Injectable({
  providedIn: 'root',
})
export class CollaborationDataService {
  private collaborationDetailsSignal = signal<Partial<CollaborationItem> | null>(null);
  private selectedDatasetsSignal = signal<Dataset[]>([]);
  private currentStepSignal = signal<number>(0);
  private errorMessageSignal = signal<string>('');
  private originalCollaborationDataSignal = signal<Partial<CollaborationItem> | null>(null);

  private step1ValidSignal = signal<boolean>(false);
  private step2ValidSignal = signal<boolean>(false);
  private step3ValidSignal = signal<boolean>(false);

  private collaborationFormData = new BehaviorSubject<Partial<CollaborationItem> | null>(null);
  collaborationData$ = this.collaborationFormData.asObservable();

  private collaborationDatasets = new BehaviorSubject<Dataset[]>([]);

  get collaborationDetails(): Partial<CollaborationItem> | null {
    return this.collaborationDetailsSignal();
  }

  set collaborationDetails(value: Partial<CollaborationItem> | null) {
    this.collaborationDetailsSignal.set(value);
    this.collaborationFormData.next(value);
  }

  get selectedDatasets(): Dataset[] {
    return this.selectedDatasetsSignal();
  }

  set selectedDatasets(value: Dataset[]) {
    this.selectedDatasetsSignal.set(value);
    this.collaborationDatasets.next(value);
  }

  get currentStep(): number {
    return this.currentStepSignal();
  }

  set currentStep(value: number) {
    this.currentStepSignal.set(value);
  }

  get errorMessage(): string {
    return this.errorMessageSignal();
  }

  set errorMessage(error: string) {
    this.errorMessageSignal.set(error);
  }

  get originalCollaborationData(): Partial<CollaborationItem> | null {
    return this.originalCollaborationDataSignal();
  }

  set originalCollaborationData(value: Partial<CollaborationItem> | null) {
    this.originalCollaborationDataSignal.set(value);
  }

  get isStep1Valid(): boolean {
    return this.step1ValidSignal();
  }

  set isStep1Valid(value: boolean) {
    this.step1ValidSignal.set(value);
  }

  get isStep2Valid(): boolean {
    return this.step2ValidSignal();
  }

  set isStep2Valid(value: boolean) {
    this.step2ValidSignal.set(value);
  }

  get isStep3Valid(): boolean {
    return this.step3ValidSignal();
  }

  set isStep3Valid(value: boolean) {
    this.step3ValidSignal.set(value);
  }

  getCompleteCollaborationData(): Partial<CollaborationItem> {
    return {
      ...this.collaborationDetails,
      ownerDatasets: this.selectedDatasets,
    };
  }

  updateCollaborationDetails(data: Partial<CollaborationItem>): void {
    const currentData = this.collaborationDetails || {};
    this.collaborationDetails = { ...currentData, ...data };
  }

  addDataset(dataset: Dataset): void {
    const currentDatasets = [...this.selectedDatasets];
    if (
      !currentDatasets.some((d) => d.dataConnectionId === dataset.dataConnectionId && d.tableName === dataset.tableName)
    ) {
      currentDatasets.push(dataset);
      this.selectedDatasets = currentDatasets;
    }
  }

  removeDataset(datasetToRemove: Dataset): void {
    const currentDatasets = this.selectedDatasets.filter(
      (d) => !(d.dataConnectionId === datasetToRemove.dataConnectionId && d.tableName === datasetToRemove.tableName),
    );
    this.selectedDatasets = currentDatasets;
  }

  clearData(): void {
    this.collaborationDetails = null;
    this.selectedDatasets = [];
    this.currentStep = 0;
    this.originalCollaborationData = null;
    this.isStep1Valid = false;
    this.isStep2Valid = false;
    this.isStep3Valid = false;
  }
}
